import React, { useMemo } from "react";
import { Button, Flex, HStack, Select, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

/**
 * TablePagination — the panel footer pager.
 * Template ref: `.foot` (39px), `.pager`, `.pageBtn`, `.pageBtn.active` (navy).
 * Renders a "Page X of Y" label + page-size select on the left and a compact
 * prev / numbered / next pager on the right.
 *
 * Props:
 *   page        - current page (1-based)
 *   pageSize    - items per page
 *   totalItems  - total item count from API
 *   totalPages  - total page count from API
 *   onPageChange(page)     - called when page changes
 *   onPageSizeChange(size) - called when page size changes
 */
const TablePagination = ({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t } = useTranslation("portal");
  const total = totalPages || 1;

  const pageNumbers = useMemo(() => {
    const current = page;
    if (total <= 1) return [1];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [1];
    const left = Math.max(2, current - 1);
    const right = Math.min(total - 1, current + 1);
    if (left > 2) pages.push("...");
    for (let p = left; p <= right; p++) pages.push(p);
    if (right < total - 1) pages.push("...");
    pages.push(total);
    return pages;
  }, [page, total]);

  if (!totalItems && !totalPages) return null;

  // Compact square pager button matching `.pageBtn`.
  const pageBtnSx = {
    height: "25px",
    minWidth: "25px",
    px: "8px",
    borderRadius: "10px",
    borderWidth: "1px",
    borderColor: "portal.line",
    bg: "white",
    color: "#667a89",
    fontSize: "12px",
    fontWeight: 700,
  };

  return (
    <Flex
      align="center"
      justify="space-between"
      h="39px"
      px="12px"
      borderTopWidth="1px"
      borderColor="portal.line"
      bg="white"
      color="#667a89"
      fontWeight={700}
      flexWrap="wrap"
      gap="8px"
    >
      <HStack spacing="8px">
        <Text fontSize="12px" whiteSpace="nowrap" sx={{ marginBottom: "0 !important" }}>
          {t("common.pagination.pageLabel", "Page {{page}} of {{total}}", {
            page,
            total,
          })}
        </Text>
        <Select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          w="auto"
          sx={{
            height: "25px !important",
            paddingTop: "0 !important",
            paddingBottom: "0 !important",
            lineHeight: "25px !important",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          {PAGE_SIZE_OPTIONS.map((sz) => (
            <option key={sz} value={sz}>
              {sz} / {t("common.pagination.page", "page")}
            </option>
          ))}
        </Select>
      </HStack>

      <HStack spacing="4px" flexWrap="wrap">
        <Button
          variant="unstyled"
          display="grid"
          placeItems="center"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          isDisabled={page <= 1}
          _hover={{ bg: "portal.soft", borderColor: "portal.primary" }}
          sx={pageBtnSx}
        >
          {t("common.pagination.prev", "Prev")}
        </Button>

        {pageNumbers.map((p, idx) =>
          typeof p === "number" ? (
            <Button
              key={`p-${p}-${idx}`}
              variant="unstyled"
              display="grid"
              placeItems="center"
              onClick={() => onPageChange(p)}
              _hover={p === page ? undefined : { bg: "portal.soft", borderColor: "portal.primary" }}
              sx={{
                ...pageBtnSx,
                ...(p === page
                  ? { bg: "portal.navy2", borderColor: "portal.navy2", color: "white" }
                  : {}),
              }}
            >
              {p}
            </Button>
          ) : (
            <Text key={`dots-${idx}`} px="4px" color="#667a89" fontSize="12px">
              …
            </Text>
          )
        )}

        <Button
          variant="unstyled"
          display="grid"
          placeItems="center"
          onClick={() => onPageChange(Math.min(total, page + 1))}
          isDisabled={page >= total}
          _hover={{ bg: "portal.soft", borderColor: "portal.primary" }}
          sx={pageBtnSx}
        >
          {t("common.pagination.next", "Next")}
        </Button>
      </HStack>
    </Flex>
  );
};

export default TablePagination;
